/**
 * God Score Command Center - Unified Decision & Reasoning Widget
 * 
 * === SESSION 57: SSoT REFACTOR - SINGLE SOURCE OF TRUTH ===
 * This component now EXCLUSIVELY consumes data from MarketProvider context.
 * NO local polling, NO REST API calls - pure passive consumption.
 * 
 * Layout:
 * ┌─────────────────────────────────────────┐
 * │ 🎯 GOD SCORE (Primary - Visual Gauge)   │
 * │    ┌──────────┐                         │
 * │    │    7     │  ← NEUTRAL              │
 * │    │  /100    │    WATCHING             │
 * │    └──────────┘                         │
 * │    [████░░░░░░░░░░░░░░░░░░░░░░░] 7%       │
 * ├─────────────────────────────────────────┤
 * │ 📊 RAW INPUTS (Secondary - Text Only)   │
 * │ HFT [0] • GAMMA [0] • dPOC [7]          │
 * │ State: IDLE │ Action: NONE              │
 * └─────────────────────────────────────────┘
 * 
 * === AUDIO ALERTS (Session 39) ===
 * Integrates useSmartAlerts hook for voice notifications when score exceeds threshold
 */

import React, { memo, useRef, useState, useEffect } from 'react';
import { useMarket } from '../contexts/MarketProvider';
import useSmartAlerts from '../hooks/useSmartAlerts';

// State badge configuration
const STATE_CONFIG = {
  IDLE: { color: 'text-slate-400 bg-slate-500/20 border-slate-500/50', label: 'IDLE' },
  PENDING_ENTRY: { color: 'text-amber-400 bg-amber-500/20 border-amber-500/50', label: 'PENDING' },
  ACTIVE: { color: 'text-green-400 bg-green-500/20 border-green-500/50', label: 'ACTIVE' },
  PENDING_EXIT: { color: 'text-orange-400 bg-orange-500/20 border-orange-500/50', label: 'EXITING' },
  COOLDOWN: { color: 'text-purple-400 bg-purple-500/20 border-purple-500/50', label: 'COOLDOWN' },
};

export const GodScoreCommandCenter = memo(({ symbol = 'NIFTY' }) => {
  // ====================================================================
  // SESSION 57: SSoT - ALL DATA FROM MARKET CONTEXT (NO LOCAL POLLING)
  // ====================================================================
  const { audioEnabled, activeSymbol, connectionStatus, getSymbolData, store } = useMarket();
  
  // SESSION 69: LKG (Last Known Good) refs to prevent flicker when VIX is stale
  const lkgGammaScore = useRef(0);
  const lkgProfileScore = useRef(0);
  const lkgHftScore = useRef(0);
  
  // SESSION 100: Per-Symbol LKG Maps for Zero-Flicker Protection
  // Prevents cross-symbol contamination and ensures butter-smooth transitions
  // Each symbol maintains its own LKG (Last Known Good) values
  const lkgMainScoreMap = useRef({});  // { NIFTY: -25, BANKNIFTY: -40, ... }
  const lkgEmaScoreMap = useRef({});   // { NIFTY: -22, BANKNIFTY: -35, ... }
  const zeroFlickerThreshold = 20;  // Reject 0 if current score > this
  
  // Use prop symbol or fall back to context activeSymbol
  const targetSymbol = symbol || activeSymbol || 'NIFTY';
  
  // SESSION 100: Get LKG for current symbol (or 0 if never seen)
  const getLkgMainScore = () => lkgMainScoreMap.current[targetSymbol] ?? 0;
  const getLkgEmaScore = () => lkgEmaScoreMap.current[targetSymbol] ?? 0;
  const setLkgMainScore = (value) => { lkgMainScoreMap.current[targetSymbol] = value; };
  const setLkgEmaScore = (value) => { lkgEmaScoreMap.current[targetSymbol] = value; };
  
  // === SSoT: Get data directly from context store ===
  const symbolData = store?.[targetSymbol] || {};
  const godScore = symbolData.godScore || {};
  const strategyPulse = symbolData.strategyPulse || {};
  const sessionStats = symbolData.sessionStats || {};  // SESSION 86d: Get sessionStats for CVD
  
  // SESSION 86b: Get app_mode from strategyPulse to override market hours checks
  // SESSION 86d: Also check for 'TD_REPLAY' mode from TrueData
  const rawAppMode = strategyPulse?.app_mode || 'LIVE';
  const appMode = rawAppMode;
  const isReplayMode = appMode === 'REPLAY' || appMode === 'TD_REPLAY';
  
  // SESSION 86b: Use WebSocket timestamp instead of browser's Date()
  // This ensures the UI clock shows the correct time during replay
  const wsTimestamp = strategyPulse?.timestamp || null;
  
  // SESSION 86d: Extract CVD from multiple sources (priority order)
  // SESSION 86f: FINAL FIX - Check ALL paths where CVD might be stored
  // SESSION 86g: ANTI-BOUNCE FIX - Prefer live_cvd (WebSocket-only) over sessionStats (API pollable)
  // SESSION 86h: STRICT EXISTENCE CHECK - Only live_cvd from pulses with real CVD data
  // 
  // PRIORITY ORDER:
  // 1. symbolData.live_cvd (WebSocket-only, immune to polling, preserved on lightweight ticks)
  // 2. strategyPulse.hft_breakdown.session_cvd_absolute (direct from pulse)
  // 3. strategyPulse.hft_breakdown.cvd (alias in pulse)
  // 4. sessionStats.cumulative_delta (fallback to API-pollable anchor)
  // 5. godScore.hft_breakdown.session_cvd_absolute (from god_score state)
  const rawCvd = symbolData?.live_cvd 
    ?? strategyPulse?.hft_breakdown?.session_cvd_absolute 
    ?? strategyPulse?.hft_breakdown?.cvd
    ?? sessionStats?.cumulative_delta
    ?? godScore?.hft_breakdown?.session_cvd_absolute 
    ?? godScore?.hft_breakdown?.cvd
    ?? 0;
  
  // SESSION 69: Extract structural decay data from hft_breakdown (for live display)
  // SESSION 70: FIX - hftBreakdown now comes from strategyPulse WebSocket data
  const hftBreakdown = strategyPulse?.hft_breakdown ?? godScore?.hft_breakdown ?? {};
  const structuralAdjustment = hftBreakdown.structural_adjustment ?? 0;
  const distanceFromFlip = hftBreakdown.distance_from_flip ?? 0;
  const isWaterfallZone = hftBreakdown.is_waterfall_zone ?? false;
  const isRocketZone = hftBreakdown.is_rocket_zone ?? false;

  // SESSION 120: Lee-Ready CVD for comparison with professional tools
  // This is a parallel calculation using industry-standard tick test algorithm
  const leeReadyCvd = strategyPulse?.hft_breakdown?.lee_ready_cvd 
    ?? godScore?.hft_breakdown?.lee_ready_cvd 
    ?? hftBreakdown?.lee_ready_cvd 
    ?? 0;
  
  // SESSION 121: LQP (Liquidity Pump) Signal from hft_breakdown
  const lqpData = hftBreakdown?.lqp ?? {};
  const lqpSignal = lqpData?.signal;
  const lqpDetected = lqpSignal?.detected ?? false;
  const lqpDirection = lqpSignal?.direction ?? '';  // "BUY" or "SELL"
  
  // SESSION 86h: Extract delta_slope with same preservation logic (AFTER hftBreakdown is defined)
  const rawDeltaSlope = symbolData?.live_delta_slope
    ?? strategyPulse?.hft_breakdown?.delta_slope
    ?? hftBreakdown?.delta_slope
    ?? godScore?.hft_breakdown?.delta_slope
    ?? 0;
  
  // SESSION 86h: Debug log CVD source (uncomment to debug)
  // console.log(`[CVD] ${targetSymbol}: live_cvd=${symbolData?.live_cvd}, pulse.hft=${strategyPulse?.hft_breakdown?.session_cvd_absolute}, sessionStats=${sessionStats?.cumulative_delta}, rawCvd=${rawCvd}`);
  
  // Market status from connection
  // SESSION 86b: Override market status when in REPLAY mode
  const rawIsMarketOpen = connectionStatus?.isMarketOpen ?? false;
  const isMarketOpen = isReplayMode ? true : rawIsMarketOpen;
  
  // SESSION 69: Market hours status from strategy pulse (for signal guardrail indicator)
  // SESSION 86b: Override when in REPLAY mode
  const marketHours = strategyPulse?.market_hours ?? {};
  const signalsEnabled = isReplayMode ? true : (marketHours?.signals_enabled ?? isMarketOpen);
  const marketHoursMessage = isReplayMode ? 'Replay Mode Active' : (marketHours?.message ?? (isMarketOpen ? 'Market Open' : 'Market Closed'));
  const marketHoursStatus = isReplayMode ? 'REPLAY_ACTIVE' : (marketHours?.status ?? (isMarketOpen ? 'MARKET_OPEN' : 'MARKET_CLOSED'));
  
  // Extract data from unified source (DERIVED STATE - auto-updates when store changes)
  const rawIncomingScore = godScore?.total_score ?? strategyPulse?.god_score ?? null;
  const direction = godScore?.direction ?? strategyPulse?.direction ?? 'NEUTRAL';
  const isVetoed = godScore?.is_vetoed ?? strategyPulse?.is_vetoed ?? false;
  const vetoReason = godScore?.veto_reason ?? strategyPulse?.veto_reason ?? '';
  const signalState = godScore?.signal_state ?? strategyPulse?.state ?? 'IDLE';
  
  // SESSION 77: Zero-Flicker Protection for Main Score
  // Rule 1: Reject incoming 0 if current LKG is heavily directional (>threshold)
  // Rule 2: Only accept 0 if signal_state is explicitly IDLE/FLAT or market closed
  // Rule 3: Natural decay to 0 is allowed (gradual approach)
  // SESSION 100: Zero-Flicker Protection now applies to BOTH LIVE and REPLAY modes
  // SESSION 100: Per-symbol LKG for butter-smooth transitions
  let score = rawIncomingScore ?? 0;  // Default to 0 if null
  let rejectedZero = false;  // Track if we rejected a zero
  const currentLkgMain = getLkgMainScore();  // Get LKG for THIS symbol
  
  if (rawIncomingScore === 0 || rawIncomingScore === null) {
    // Check if this is a legitimate zero or a glitch
    const isLegitimateZero = 
      signalState === 'IDLE' || 
      signalState === 'FLAT' ||
      signalState === 'BLOCKED' ||  // Vetoed scores can be 0
      !isMarketOpen ||              // Market closed = legitimate 0
      Math.abs(currentLkgMain) <= zeroFlickerThreshold;  // Already near 0
    
    if (!isLegitimateZero && Math.abs(currentLkgMain) > zeroFlickerThreshold) {
      // Reject the zero - use LKG instead
      score = currentLkgMain;
      rejectedZero = true;
      // console.debug(`[ZERO-FLICKER] ${targetSymbol}: Rejected 0, using LKG:`, currentLkgMain);
    }
  }
  
  // SESSION 100 FIX: Only update LKG if we ACCEPTED the incoming score
  // Don't update LKG with rejected zeros - that defeats the purpose!
  if (rawIncomingScore !== null && rawIncomingScore !== undefined && !rejectedZero) {
    setLkgMainScore(rawIncomingScore);
  }
  
  // SESSION 69: Extract final threshold from godScore or hft_breakdown or strategyPulse
  // SESSION 70: FIX - Added strategyPulse.threshold as primary source for WebSocket data
  const finalThreshold = strategyPulse?.threshold ?? godScore?.threshold ?? hftBreakdown?.final_threshold ?? 80;
  const thresholdReason = strategyPulse?.threshold_reason ?? godScore?.threshold_reason ?? hftBreakdown?.threshold_reason ?? '';
  
  // SESSION 70: Bimodal Persistence - Extract EMA score and position state
  const bimodalData = strategyPulse?.bimodal ?? {};
  const rawScore = bimodalData?.raw_score ?? godScore?.raw_score ?? score;
  
  // SESSION 77: Zero-Flicker Protection for EMA Score
  // SESSION 100: Per-symbol LKG for butter-smooth transitions
  const rawIncomingEma = bimodalData?.ema_score ?? godScore?.ema_score ?? hftBreakdown?.ema_god_score ?? null;
  let emaScore = rawIncomingEma;
  let rejectedEmaZero = false;
  const currentLkgEma = getLkgEmaScore();  // Get LKG for THIS symbol
  
  if (rawIncomingEma === 0 || rawIncomingEma === null) {
    // Apply same zero-flicker logic to EMA
    const isLegitimateZero = 
      signalState === 'IDLE' || 
      signalState === 'FLAT' ||
      !isMarketOpen ||
      Math.abs(currentLkgEma) <= zeroFlickerThreshold;
    
    if (!isLegitimateZero && Math.abs(currentLkgEma) > zeroFlickerThreshold) {
      emaScore = currentLkgEma;
      rejectedEmaZero = true;
    }
  }
  
  // SESSION 100 FIX: Only update EMA LKG if we ACCEPTED the incoming score
  if (rawIncomingEma !== null && rawIncomingEma !== undefined && !rejectedEmaZero) {
    setLkgEmaScore(rawIncomingEma);
  }
  
  // Fall back to main score if EMA is still null
  // SESSION 77: Ensure emaScore is always a number to prevent .toFixed() errors
  if (emaScore === null || emaScore === undefined) {
    emaScore = score ?? 0;
  }
  
  const atrValue = bimodalData?.atr_value ?? godScore?.atr_value ?? hftBreakdown?.atr_value ?? 0;
  const trailingSL = bimodalData?.trailing_sl ?? godScore?.trailing_sl ?? hftBreakdown?.trailing_sl ?? null;
  const positionState = bimodalData?.position_state ?? null;
  const maxFavorablePrice = positionState?.max_favorable_price ?? null;
  
  // SESSION 70: Last exit info (from WebSocket bimodal_exit event)
  const lastExit = bimodalData?.last_exit ?? symbolData?.lastBimodalExit ?? null;
  
  // Component scores - SESSION 69: LKG Pattern to prevent VIX-caused flicker
  // Rule: Only update LKG if we have a non-zero value OR if godScore has explicit data
  const rawHftScore = godScore?.hft_score ?? strategyPulse?.components?.hft;
  const rawGammaScore = godScore?.gamma_score ?? godScore?.gamma_breakdown?.gex_score ?? strategyPulse?.components?.gamma;
  const rawProfileScore = godScore?.profile_score ?? godScore?.profile_breakdown?.dpoc_score ?? strategyPulse?.components?.profile;
  
  // Update LKG refs only if we have valid (non-null) data
  if (rawHftScore !== undefined && rawHftScore !== null) lkgHftScore.current = rawHftScore;
  if (rawGammaScore !== undefined && rawGammaScore !== null) lkgGammaScore.current = rawGammaScore;
  if (rawProfileScore !== undefined && rawProfileScore !== null) lkgProfileScore.current = rawProfileScore;
  
  // SESSION 69: Track if we've ever received real data
  const hasReceivedGammaData = rawGammaScore !== undefined && rawGammaScore !== null;
  const hasReceivedProfileData = rawProfileScore !== undefined && rawProfileScore !== null;
  const hasReceivedHftData = rawHftScore !== undefined && rawHftScore !== null;
  
  // Use LKG values - this prevents flicker to 0 when VIX is stale
  // SESSION 77: Fall back to 0 (not null) to prevent .toFixed() errors
  const hftScore = rawHftScore ?? lkgHftScore.current ?? 0;
  const gammaScore = rawGammaScore ?? lkgGammaScore.current ?? 0;
  const profileScore = rawProfileScore ?? lkgProfileScore.current ?? 0;
  
  // SESSION 113: Component Breakdown for GodScore Analysis
  // Extract individual component scores from hft_breakdown
  const componentScores = {
    order_flow: hftBreakdown?.order_flow_score ?? strategyPulse?.components?.order_flow ?? 0,
    profile_context: hftBreakdown?.profile_context_score ?? strategyPulse?.components?.profile_context ?? 0,
    profile_levels: hftBreakdown?.profile_levels_score ?? strategyPulse?.components?.profile_levels ?? 0,
    gex: hftBreakdown?.gex_score ?? strategyPulse?.components?.gex ?? 0,
  };
  
  // SESSION 113: Track previous component scores to detect biggest mover
  const prevComponentScores = useRef({ order_flow: 0, profile_context: 0, profile_levels: 0, gex: 0 });
  const componentHistory = useRef([]);  // Track last N readings for volatility
  
  // Calculate component changes
  const componentDeltas = {
    order_flow: componentScores.order_flow - prevComponentScores.current.order_flow,
    profile_context: componentScores.profile_context - prevComponentScores.current.profile_context,
    profile_levels: componentScores.profile_levels - prevComponentScores.current.profile_levels,
    gex: componentScores.gex - prevComponentScores.current.gex,
  };
  
  // Find biggest mover (largest absolute change)
  const biggestMover = Object.entries(componentDeltas).reduce((max, [key, delta]) => {
    return Math.abs(delta) > Math.abs(max.delta) ? { key, delta } : max;
  }, { key: null, delta: 0 });
  
  // Update previous scores for next comparison
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    prevComponentScores.current = { ...componentScores };
    // Track history for volatility (keep last 10 readings)
    componentHistory.current = [...componentHistory.current.slice(-9), { ...componentScores, timestamp: Date.now() }];
  }, [componentScores.order_flow, componentScores.profile_context, componentScores.profile_levels, componentScores.gex]);
  
  // Calculate volatility (std dev of changes over history)
  const getComponentVolatility = (key) => {
    if (componentHistory.current.length < 3) return 'stable';
    const values = componentHistory.current.map(h => h[key]);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    if (stdDev > 15) return 'volatile';
    if (stdDev > 5) return 'moderate';
    return 'stable';
  };
  
  // Component display config
  const componentConfig = {
    order_flow: { label: 'Order Flow', weight: '25%', icon: '📊' },
    profile_context: { label: 'Profile Ctx', weight: '25%', icon: '📈' },
    profile_levels: { label: 'Profile Lvl', weight: '30%', icon: '🎯' },
    gex: { label: 'GEX', weight: '20%', icon: '⚡' },
  };

  // SESSION 114: SHADOW MODE - TPO-Based GodScore for comparison
  // This allows users to see what the score WOULD be with architectural fix
  // SESSION 120 FIX: Get scoring_mode to determine if shadow panel should be shown
  // When scoring_mode=TPO (default), we're already using TPO - shadow is redundant
  const scoringMode = strategyPulse?.scoring_mode ?? godScore?.hft_breakdown?.scoring_mode ?? 'TPO';
  const isTpoMode = scoringMode === 'TPO';
  
  const shadowData = strategyPulse?.shadow ?? godScore?.shadow_breakdown ?? {};
  const shadowEnabled = strategyPulse?.shadow_enabled ?? godScore?.shadow_enabled ?? false;
  const tpoAvailable = shadowData?.tpo_available ?? false;
  const shadowEmaScore = strategyPulse?.shadow_ema_score ?? godScore?.shadow_ema_score ?? 0;
  const shadowDirection = strategyPulse?.shadow_direction ?? godScore?.shadow_direction ?? 'NEUTRAL';
  const shadowSignalState = strategyPulse?.shadow_signal_state ?? godScore?.shadow_signal_state ?? 'NEUTRAL';
  const shadowWouldTrigger = strategyPulse?.shadow_would_trigger ?? godScore?.shadow_would_trigger ?? false;
  
  // POC Comparison
  const volumePoc = shadowData?.volume_poc ?? strategyPulse?.volume_poc ?? godScore?.volume_poc ?? 0;
  const tpoPoc = shadowData?.tpo_poc ?? strategyPulse?.tpo_poc ?? godScore?.tpo_poc ?? 0;
  const pocDivergence = shadowData?.poc_divergence_pts ?? strategyPulse?.poc_divergence ?? godScore?.poc_divergence ?? 0;
  
  // Shadow Component Scores
  const shadowComponents = {
    order_flow: shadowData?.components?.order_flow ?? strategyPulse?.shadow_order_flow_score ?? godScore?.shadow_order_flow_score ?? 0,
    profile_context: shadowData?.components?.profile_context?.shadow ?? strategyPulse?.shadow_profile_context_score ?? godScore?.shadow_profile_context_score ?? 0,
    profile_levels: shadowData?.components?.profile_levels?.shadow ?? strategyPulse?.shadow_profile_levels_score ?? godScore?.shadow_profile_levels_score ?? 0,
    gex: shadowData?.components?.gex ?? strategyPulse?.shadow_gex_score ?? godScore?.shadow_gex_score ?? 0,
  };
  
  // Calculate component diffs (Shadow - Active)
  const shadowComponentDiffs = {
    order_flow: shadowComponents.order_flow - componentScores.order_flow,
    profile_context: shadowComponents.profile_context - componentScores.profile_context,
    profile_levels: shadowComponents.profile_levels - componentScores.profile_levels,
    gex: shadowComponents.gex - componentScores.gex,
  };
  
  // NOTE: hasDivergenceAlert moved to after isExecuteReady is defined (line ~420)

  // === VIX & Dynamic Threshold (Session 39) ===
  // SESSION 69: VIX status should NOT affect GEX display
  const vixValue = godScore?.vix_value ?? null;
  const vixRegime = godScore?.vix_regime ?? 'UNAVAILABLE';
  const vixIsStale = godScore?.vix_is_stale ?? true;  // Default to stale until proven otherwise
  const dynamicThreshold = godScore?.threshold ?? 80;
  // thresholdReason already defined above
  
  // Execution state
  const state = strategyPulse?.state ?? 'IDLE';
  const action = strategyPulse?.action ?? 'NONE';
  const position = strategyPulse?.position;
  const pnl = strategyPulse?.active_trade_pnl ?? 0;
  
  // SESSION 81: Position State Machine & Structural Exits
  // Safe fallback to FLAT if WebSocket payload is missing (e.g., first tick of Replay)
  const session81 = strategyPulse?.session_81 ?? {};
  const positionStateMachine = session81?.position_state ?? 'FLAT';
  const structuralSL = session81?.structural_sl ?? 0;
  const liquidityTarget = session81?.liquidity_target ?? 0;
  
  // SESSION 89: Trailing SL Data for UI Visualizer
  const trailingSLActive = session81?.trailing_sl_active ?? false;
  const trailingSLPrice = session81?.trailing_sl_price ?? 0;
  const highTrendConviction = session81?.high_trend_conviction ?? false;
  
  // SESSION 82: Initial Balance (IB) & Market Regime
  // Safe fallback to FORMING_IB if WebSocket payload is missing
  const session82 = strategyPulse?.session_82 ?? {};
  const marketRegime = session82?.market_regime ?? 'FORMING_IB';
  const ibHigh = session82?.ib_high ?? 0;
  const ibLow = session82?.ib_low ?? 0;
  const ibWidth = session82?.ib_width ?? 0;
  const ibLocked = session82?.ib_locked ?? false;
  
  // SESSION 82: Market Regime styling
  const REGIME_CONFIG = {
    FORMING_IB: { color: 'bg-amber-500/20 border-amber-500/50 text-amber-400', label: 'FORMING IB', icon: '⏳' },
    TREND_MODE: { color: 'bg-green-500/20 border-green-500/50 text-green-400', label: 'TREND MODE', icon: '🚀' },
    CHOP_MODE: { color: 'bg-red-500/20 border-red-500/50 text-red-400', label: 'CHOP MODE', icon: '🔄' },
    NORMAL: { color: 'bg-blue-500/20 border-blue-500/50 text-blue-400', label: 'NORMAL', icon: '📊' }
  };
  const regimeConfig = REGIME_CONFIG[marketRegime] || REGIME_CONFIG.FORMING_IB;
  
  // SESSION 81: Position state styling
  const POSITION_STATE_CONFIG = {
    FLAT: { color: 'bg-slate-500/20 border-slate-500/50 text-slate-400', label: 'FLAT', icon: '—' },
    LONG: { color: 'bg-green-500/30 border-green-500/60 text-green-400', label: 'LONG', icon: '↑' },
    SHORT: { color: 'bg-red-500/30 border-red-500/60 text-red-400', label: 'SHORT', icon: '↓' }
  };
  const posStateConfig = POSITION_STATE_CONFIG[positionStateMachine] || POSITION_STATE_CONFIG.FLAT;
  const isInPosition = positionStateMachine === 'LONG' || positionStateMachine === 'SHORT';
  
  // SESSION 83: Historical Memory & Naked POCs
  // Safe fallback to empty array if WebSocket payload is missing
  const session83 = strategyPulse?.session_83 ?? {};
  const nakedPocs = session83?.naked_pocs ?? [];
  const nakedPocsCount = session83?.naked_pocs_count ?? 0;
  const historicalDaysLoaded = session83?.historical_days_loaded ?? 0;
  
  // SESSION 91: Price validation - prevent stale price flash
  // Use a stable price source with validation
  const rawPrice = godScore?.structure_debug?.price ?? strategyPulse?.market_data?.price ?? godScore?.price ?? symbolData.futuresPrice ?? 0;
  
  // Validate price is reasonable for the symbol
  const isValidPrice = React.useMemo(() => {
    if (!rawPrice || rawPrice <= 0) return false;
    // NIFTY range: 15000-35000, BANKNIFTY range: 35000-75000
    if (symbol === 'NIFTY') {
      return rawPrice > 15000 && rawPrice < 35000;
    } else if (symbol === 'BANKNIFTY') {
      return rawPrice > 35000 && rawPrice < 75000;
    }
    return rawPrice > 0;
  }, [rawPrice, symbol]);
  
  // Use validated price or previous valid price
  const [lastValidPrice, setLastValidPrice] = React.useState(0);
  React.useEffect(() => {
    if (isValidPrice && rawPrice !== lastValidPrice) {
      setLastValidPrice(rawPrice);
    }
  }, [rawPrice, isValidPrice, lastValidPrice]);
  
  const price = isValidPrice ? rawPrice : lastValidPrice;
  
  // Thresholds - USE DYNAMIC THRESHOLD from VIX
  const threshold = dynamicThreshold;
  const isExecuteReady = score >= threshold && !isVetoed;
  const isBuilding = score >= (threshold - 20) && score < threshold;
  
  // Check if there's a divergence alert (Shadow would trigger but Active doesn't)
  // SESSION 117c: Moved here from line 328 to fix "Cannot access 'sr' before initialization"
  const hasDivergenceAlert = shadowWouldTrigger && !isExecuteReady;
  
  // SESSION 58: Check if L2 data is pending (for INCOMPLETE state warning)
  // SESSION 69: Hot-wire to use L1 HFT data as fallback when L2 is missing
  const hasL1OrderFlow = !!godScore?.hft_breakdown;
  const isL2DataPending = !godScore?.imbalance_zones && !hasL1OrderFlow;
  
  // State styling
  const stateConfig = STATE_CONFIG[state] || STATE_CONFIG.IDLE;
  
  // Signal status text
  // SESSION 58: Show INCOMPLETE if L2 data pending and God Score is low
  const getStatusText = () => {
    if (isVetoed) return 'BLOCKED';
    if (isExecuteReady) return 'EXECUTE READY';
    if (isBuilding) return 'BUILDING';
    if (isL2DataPending && score === 0) return 'INCOMPLETE';
    return 'WATCHING';
  };

  // Progress bar percentage - scale relative to dynamic threshold
  const progressPercent = Math.min(100, Math.max(0, (score / threshold) * 100));
  
  // Progress bar color
  // SESSION 58: Show amber if L2 data pending
  const getProgressColor = () => {
    if (isExecuteReady) return 'bg-green-500';
    if (isBuilding) return 'bg-amber-500';
    if (isL2DataPending && score === 0) return 'bg-amber-500/50';
    return 'bg-slate-500';
  };
  
  // VIX regime color - gray for stale data
  const getVixColor = () => {
    if (vixIsStale) return 'text-gray-500';
    if (vixRegime === 'LOW') return 'text-green-400';
    if (vixRegime === 'HIGH') return 'text-red-400';
    return 'text-amber-400';
  };
  
  // === SMART AUDIO ALERTS (Session 39, Updated Session 40) ===
  // Trigger voice alert when God Score crosses the dynamic threshold
  // CRITICAL: Pass isVetoed to prevent false baits - voice only speaks on VALID trades
  useSmartAlerts(score, targetSymbol, threshold, audioEnabled, isVetoed, vetoReason);

  // SESSION 71: Removed debug toast notifications (Score vs Gate math display)
  // Signal visibility is now handled exclusively by the Global Signal Log

  return (
    <div className="space-y-2">
      {/* ============================================
          PRIMARY WIDGET: GOD SCORE (Visual Gauge)
          ============================================ */}
      <div className={`rounded border p-2.5 pb-3 ${
        isExecuteReady 
          ? 'bg-green-500/10 border-green-500/50' 
          : isBuilding 
            ? 'bg-amber-500/10 border-amber-500/50' 
            : isVetoed
              ? 'bg-orange-500/10 border-orange-500/50'
              : 'bg-surface/50 border-border'
      }`}>
        {/* Header Row: Symbol + State Badge - BULLETPROOF flex-nowrap layout */}
        <div className="flex flex-row flex-nowrap items-center justify-between gap-2 mb-2 overflow-hidden">
          <div className="flex flex-row flex-nowrap items-center gap-2 min-w-[100px]">
            <span className="text-xs font-bold text-accent shrink-0 min-w-[50px]">{targetSymbol}</span>
            <div className={`px-2 py-0.5 rounded border text-[9px] font-bold whitespace-nowrap shrink-0 ${stateConfig.color}`}>
              {stateConfig.label}
            </div>
            {/* SESSION 120: Show scoring mode indicator */}
            {isTpoMode && (
              <span className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-purple-500/20 text-purple-300 border border-purple-500/40 whitespace-nowrap shrink-0">
                TPO Mode
              </span>
            )}
          </div>
          <span className="text-[9px] text-muted-foreground whitespace-nowrap shrink-0">Threshold: {threshold}</span>
        </div>

        {/* SESSION 81: EXECUTION STATE BADGE - Position State Machine (FLAT/LONG/SHORT) */}
        <div className={`flex items-center justify-center gap-3 px-3 py-2 mb-2 rounded border transition-all duration-300 ${posStateConfig.color}`} data-testid="execution-state-badge">
          <span className="text-[10px] font-medium text-muted-foreground">POSITION:</span>
          <div className="flex items-center gap-1.5">
            <span className={`text-lg font-bold ${posStateConfig.color.includes('green') ? 'text-green-400' : posStateConfig.color.includes('red') ? 'text-red-400' : 'text-slate-400'}`}>
              {posStateConfig.icon}
            </span>
            <span className={`text-base font-bold font-mono tracking-wide ${posStateConfig.color.includes('green') ? 'text-green-400' : posStateConfig.color.includes('red') ? 'text-red-400' : 'text-slate-400'}`}>
              {posStateConfig.label}
            </span>
          </div>
          {/* One-Trade-Rule indicator */}
          <span className="text-[8px] text-muted-foreground ml-auto">
            {session81?.one_trade_rule === 'ENFORCED' && '🔒 1-TRADE'}
          </span>
        </div>

        {/* SESSION 82: MARKET REGIME BADGE - IB-based Day Type Classification */}
        <div className={`flex items-center justify-between px-3 py-2 mb-2 rounded border transition-all duration-300 ${regimeConfig.color}`} data-testid="market-regime-badge">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium text-muted-foreground">REGIME:</span>
            <span className="text-sm font-bold">
              {regimeConfig.icon} {regimeConfig.label}
            </span>
          </div>
          {/* IB Width indicator (only show if IB locked) */}
          {ibLocked && ibWidth > 0 && (
            <span className="text-[9px] font-mono text-muted-foreground">
              IB: {ibWidth.toFixed(0)} pts
            </span>
          )}
        </div>

        {/* SESSION 69: Market Closed Indicator - Shows when signals are disabled */}
        {!signalsEnabled && (
          <div className="flex items-center justify-center gap-2 px-2 py-1.5 mb-2 bg-amber-500/10 border border-amber-500/30 rounded" data-testid="market-closed-indicator">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-[10px] font-medium text-amber-400">
              {marketHoursMessage || 'Market Closed - Monitoring Only'}
            </span>
          </div>
        )}

        {/* SESSION 69: Structural Intelligence Display - Live Monitoring */}
        <div className="flex items-center justify-between px-2 py-1.5 mb-1 bg-black/30 rounded border border-white/5">
          <div className="flex items-center gap-3">
            {/* Structural Adjustment */}
            <div className="flex items-center gap-1">
              <span className="text-[8px] text-gray-500">Struct:</span>
              <span className={`text-[10px] font-mono font-bold ${
                structuralAdjustment > 0 ? 'text-green-400' : 
                structuralAdjustment < 0 ? 'text-red-400' : 'text-gray-400'
              }`}>
                {structuralAdjustment > 0 ? '+' : ''}{structuralAdjustment.toFixed(1)}
              </span>
            </div>
            {/* Flip Distance */}
            <div className="flex items-center gap-1">
              <span className="text-[8px] text-gray-500">Flip:</span>
              <span className={`text-[10px] font-mono font-bold ${
                distanceFromFlip > 0 ? 'text-green-400' : 
                distanceFromFlip < 0 ? 'text-red-400' : 'text-gray-400'
              }`}>
                {distanceFromFlip > 0 ? '+' : ''}{distanceFromFlip.toFixed(0)}
              </span>
            </div>
          </div>
          {/* Zone Badge */}
          {(isRocketZone || isWaterfallZone) && (
            <span className={`px-1.5 py-0.5 text-[8px] font-bold rounded ${
              isRocketZone 
                ? 'bg-green-500/20 text-green-300 border border-green-500/40' 
                : 'bg-red-500/20 text-red-300 border border-red-500/40'
            }`}>
              {isRocketZone ? 'ROCKET' : 'WATERFALL'}
            </span>
          )}
        </div>

        {/* SESSION 70: TARGET GATE - Dedicated row for threshold display */}
        <div className={`flex items-center justify-center gap-2 px-2 py-1.5 mb-2 rounded border transition-all duration-300 ${
          Math.abs(score) >= finalThreshold
            ? 'bg-green-500/15 border-green-500/40'
            : Math.abs(score) >= finalThreshold - 5
              ? 'bg-yellow-500/15 border-yellow-500/40 animate-pulse'
              : (isRocketZone || isWaterfallZone)
                ? 'bg-amber-500/10 border-amber-500/30'
                : 'bg-black/20 border-white/5'
        }`}>
          <span className="text-[9px] text-gray-400">TARGET GATE:</span>
          <span className={`text-[12px] font-mono font-bold ${
            Math.abs(score) >= finalThreshold
              ? 'text-green-400'
              : Math.abs(score) >= finalThreshold - 5
                ? 'text-yellow-400'
                : (isRocketZone || isWaterfallZone)
                  ? 'text-amber-400'
                  : 'text-accent'
          }`}>
            ±{Math.round(finalThreshold)}
          </span>
          {Math.abs(score) >= finalThreshold && (
            <span className="text-[8px] text-green-400 font-semibold">TRIGGERED</span>
          )}
        </div>

        {/* Main Score Display: Circular Gauge - Compact */}
        <div className="flex items-center justify-center gap-3">
          {/* Circular Gauge - Smaller */}
          <div className="relative">
            <svg className="w-16 h-16 transform -rotate-90">
              {/* Background circle */}
              <circle
                cx="32"
                cy="32"
                r="26"
                fill="none"
                stroke="currentColor"
                strokeWidth="5"
                className="text-surface-highlight"
              />
              {/* Progress circle */}
              <circle
                cx="32"
                cy="32"
                r="26"
                fill="none"
                stroke="currentColor"
                strokeWidth="5"
                strokeDasharray={`${(progressPercent / 100) * 163} 163`}
                strokeLinecap="round"
                className={`${
                  isExecuteReady 
                    ? 'text-green-500' 
                    : isBuilding 
                      ? 'text-amber-500' 
                      : isVetoed
                        ? 'text-orange-500'
                        : 'text-slate-500'
                } transition-all duration-500`}
              />
            </svg>
            {/* Score inside circle */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-lg font-bold font-mono ${
                isExecuteReady ? 'text-green-400' : 
                isBuilding ? 'text-amber-400' : 
                isVetoed ? 'text-orange-400' :
                'text-foreground'
              }`}>
                {Math.round(score)}
              </span>
              <span className="text-[8px] text-muted-foreground">/100</span>
            </div>
          </div>
          
          {/* SESSION 70: Bimodal Score Display - Show EMA below circle */}
          <div className="text-center mt-1">
            <span className="text-[8px] text-purple-400 font-medium">
              EMA: <span className="font-mono font-bold text-purple-300">{(emaScore ?? score ?? 0).toFixed(1)}</span>
            </span>
          </div>
          
          {/* Direction + Status */}
          {/* SESSION 104: Color scheme update
              - NEUTRAL / BLOCKED states: Grey (slate-400)
              - LONG EXECUTE: Deep Green (green-500)
              - SHORT EXECUTE: Deep Red (red-500)
              - BUILDING states: Amber tint
          */}
          <div className="text-left">
            <div className={`text-base font-bold ${
              // SESSION 104: Deep colors for EXECUTE, grey for blocked/neutral
              isExecuteReady && (direction === 'LONG' || direction === 'UP') ? 'text-green-500' :
              isExecuteReady && (direction === 'SHORT' || direction === 'DOWN') ? 'text-red-500' :
              isVetoed ? 'text-slate-400' :  // BLOCKED = grey regardless of direction
              isBuilding && (direction === 'LONG' || direction === 'UP') ? 'text-green-400/70' :
              isBuilding && (direction === 'SHORT' || direction === 'DOWN') ? 'text-red-400/70' :
              'text-slate-400'  // NEUTRAL or no clear signal
            }`}>
              {(direction === 'LONG' || direction === 'UP') ? '↑ LONG' : 
               (direction === 'SHORT' || direction === 'DOWN') ? '↓ SHORT' : 
               '— NEUTRAL'}
            </div>
            <div className={`text-[10px] font-medium ${
              isExecuteReady ? 'text-green-400' : 
              isBuilding ? 'text-amber-400' : 
              isVetoed ? 'text-orange-400' :
              'text-muted-foreground'
            }`}>
              {getStatusText()}
            </div>
            {/* SESSION 60: Removed debug veto text from main gauge - moved to Gates section below */}
          </div>
          
          {/* SESSION 121: LQP Badge */}
          {lqpDetected && (
            <div className={`px-3 py-1 rounded-full text-xs font-bold animate-pulse ${
              lqpDirection === 'BUY' 
                ? 'bg-cyan-500/20 border border-cyan-500/50 text-cyan-400' 
                : 'bg-orange-500/20 border border-orange-500/50 text-orange-400'
            }`} data-testid="lqp-badge">
              {lqpDirection === 'BUY' ? '🚀 LQP BUY' : '🔻 LQP SELL'}
            </div>
          )}
        </div>

        {/* Progress Bar - Compact */}
        <div className="mt-2">
          <div className="flex justify-between text-[9px] text-muted-foreground mb-0.5">
            <span>Progress</span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <div className="h-1.5 bg-surface-highlight rounded-full overflow-hidden">
            <div 
              className={`h-full ${getProgressColor()} transition-all duration-500 rounded-full`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {/* Threshold marker */}
          <div className="relative h-0">
            <div 
              className="absolute -top-1.5 w-0.5 h-1.5 bg-white/50"
              style={{ left: `${threshold}%` }}
            />
          </div>
        </div>
      </div>

      {/* ============================================
          SESSION 113: GODSCORE COMPONENT BREAKDOWN
          Shows individual component scores with volatility indicators
          ============================================ */}
      <div className="bg-surface/30 border border-border/50 rounded p-2 mt-2" data-testid="godscore-breakdown">
        {/* Header with total change indicator */}
        <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-border/30">
          <span className="text-[9px] uppercase text-muted-foreground font-semibold tracking-wide">
            Score Components
          </span>
          {biggestMover.key && Math.abs(biggestMover.delta) > 3 && (
            <span className={`text-[8px] px-1.5 py-0.5 rounded font-medium ${
              biggestMover.delta > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
            }`}>
              {componentConfig[biggestMover.key]?.label} {biggestMover.delta > 0 ? '+' : ''}{biggestMover.delta.toFixed(1)}
            </span>
          )}
        </div>

        {/* Component Bars */}
        <div className="space-y-1.5">
          {Object.entries(componentConfig).map(([key, config]) => {
            const value = componentScores[key] || 0;
            const delta = componentDeltas[key] || 0;
            const volatility = getComponentVolatility(key);
            const isBiggestMover = biggestMover.key === key && Math.abs(biggestMover.delta) > 3;
            
            // Calculate bar width (scale to ±50 range, map to 0-100%)
            const normalizedValue = Math.min(50, Math.max(-50, value));
            const barWidth = Math.abs(normalizedValue) * 2; // 50 = 100%, 25 = 50%
            const isPositive = value >= 0;
            
            return (
              <div key={key} className={`${isBiggestMover ? 'bg-yellow-500/10 rounded px-1 py-0.5 -mx-1' : ''}`}>
                {/* Label row */}
                <div className="flex items-center justify-between text-[8px] mb-0.5">
                  <div className="flex items-center gap-1">
                    <span>{config.icon}</span>
                    <span className={`font-medium ${isBiggestMover ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                      {config.label}
                    </span>
                    <span className="text-[7px] text-muted-foreground/70">({config.weight})</span>
                    {/* Volatility indicator */}
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      volatility === 'volatile' ? 'bg-red-500 animate-pulse' :
                      volatility === 'moderate' ? 'bg-yellow-500' :
                      'bg-green-500'
                    }`} title={`Volatility: ${volatility}`}></span>
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Delta indicator */}
                    {Math.abs(delta) > 1 && (
                      <span className={`text-[7px] font-mono ${delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {delta > 0 ? '↑' : '↓'}{Math.abs(delta).toFixed(0)}
                      </span>
                    )}
                    {/* Value */}
                    <span className={`font-mono font-bold ${
                      value > 10 ? 'text-green-400' : 
                      value < -10 ? 'text-red-400' : 
                      'text-muted-foreground'
                    }`}>
                      {value > 0 ? '+' : ''}{value.toFixed(1)}
                    </span>
                  </div>
                </div>
                {/* Bar visualization */}
                <div className="h-1.5 bg-surface-highlight rounded-full overflow-hidden relative">
                  {/* Center line */}
                  <div className="absolute left-1/2 top-0 w-px h-full bg-white/20 z-10"></div>
                  {/* Value bar */}
                  <div 
                    className={`h-full transition-all duration-300 ${
                      isPositive ? 'bg-green-500 ml-[50%]' : 'bg-red-500'
                    }`}
                    style={{ 
                      width: `${barWidth / 2}%`,
                      marginLeft: isPositive ? '50%' : `${50 - barWidth / 2}%`
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Biggest Mover Alert (when significant change detected) */}
        {biggestMover.key && Math.abs(biggestMover.delta) > 10 && (
          <div className={`mt-2 px-2 py-1.5 rounded text-[8px] border ${
            biggestMover.delta > 0 
              ? 'bg-green-500/10 border-green-500/30 text-green-400' 
              : 'bg-red-500/10 border-red-500/30 text-red-400'
          }`}>
            <span className="font-semibold">⚠️ BIGGEST CHANGE:</span>{' '}
            <span className="font-mono">{componentConfig[biggestMover.key]?.label}</span>{' '}
            <span className="font-bold">{biggestMover.delta > 0 ? '+' : ''}{biggestMover.delta.toFixed(1)}</span>
            {biggestMover.key === 'profile_levels' && (
              <span className="text-muted-foreground ml-1">
                (Price crossed key level)
              </span>
            )}
            {biggestMover.key === 'order_flow' && (
              <span className="text-muted-foreground ml-1">
                (CVD/Flow shift)
              </span>
            )}
            {biggestMover.key === 'gex' && (
              <span className="text-muted-foreground ml-1">
                (GEX flip zone)
              </span>
            )}
          </div>
        )}
      </div>

      {/* ============================================
          SESSION 114: SHADOW MODE - TPO vs Volume POC Comparison
          SESSION 120 FIX: Only show when VOLUME scoring is active (legacy mode)
          When TPO is active (default/correct), shadow comparison is redundant
          ============================================ */}
      {!isTpoMode && (
      <div className={`bg-purple-900/20 border rounded p-2 mt-2 ${
        hasDivergenceAlert 
          ? 'border-yellow-500/50 animate-pulse' 
          : 'border-purple-500/30'
      }`} data-testid="shadow-mode-panel">
        {/* Header with Divergence Alert */}
        <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-purple-500/30">
          <div className="flex items-center gap-2">
            <span className="text-[9px] uppercase text-purple-400 font-semibold tracking-wide">
              👥 Shadow Mode (TPO-Based)
            </span>
            {hasDivergenceAlert && (
              <span className="text-[8px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/40 animate-bounce">
                ⚡ DIVERGENCE
              </span>
            )}
            {!tpoAvailable && (
              <span className="text-[8px] px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-400 border border-slate-500/40">
                Awaiting TPO Data
              </span>
            )}
          </div>
          <span className="text-[8px] text-purple-300/60">
            Architectural Fix Preview
          </span>
        </div>

        {/* Show waiting message if TPO not available */}
        {!tpoAvailable ? (
          <div className="text-center py-4 text-purple-400/60 text-sm">
            <div className="mb-2">⏳ TPO Profile Building...</div>
            <div className="text-[10px] text-purple-400/40">
              Shadow scores will appear once TPO data is available (requires live market data)
            </div>
          </div>
        ) : (
          <>
            {/* Score Comparison */}
            <div className="grid grid-cols-2 gap-2 mb-2">
              {/* Active Score (Volume-Based) */}
              <div className="text-center p-2 bg-slate-500/10 border border-slate-500/30 rounded">
                <div className="text-[7px] text-slate-400 font-medium uppercase mb-1">Active (Volume POC)</div>
                <div className={`text-lg font-mono font-bold ${
                  emaScore > 10 ? 'text-green-400' : emaScore < -10 ? 'text-red-400' : 'text-slate-400'
                }`}>
                  {(emaScore ?? 0).toFixed(1)}
                </div>
                <div className={`text-[8px] mt-1 ${
                  isExecuteReady ? 'text-green-400' : 'text-slate-400'
                }`}>
                  {isExecuteReady ? '✓ WOULD SIGNAL' : '— Silent'}
                </div>
              </div>

              {/* Shadow Score (TPO-Based) */}
              <div className={`text-center p-2 rounded border ${
              shadowWouldTrigger 
                ? 'bg-purple-500/20 border-purple-500/50' 
                : 'bg-purple-500/10 border-purple-500/30'
            }`}>
              <div className="text-[7px] text-purple-400 font-medium uppercase mb-1">Shadow (TPO POC)</div>
              <div className={`text-lg font-mono font-bold ${
                shadowEmaScore > 10 ? 'text-green-400' : shadowEmaScore < -10 ? 'text-red-400' : 'text-purple-400'
              }`}>
                {shadowEmaScore.toFixed(1)}
              </div>
              <div className={`text-[8px] mt-1 ${
                shadowWouldTrigger ? 'text-purple-300' : 'text-purple-400/60'
              }`}>
                {shadowWouldTrigger ? '⚡ WOULD SIGNAL' : '— Silent'}
              </div>
            </div>
          </div>

          {/* POC Comparison */}
          <div className="flex items-center justify-between px-2 py-1.5 bg-black/30 rounded mb-2">
            <div className="flex items-center gap-2">
              <span className="text-[8px] text-muted-foreground">POC:</span>
              <span className="text-[9px] font-mono">
                <span className="text-slate-400">Vol={volumePoc > 0 ? volumePoc.toFixed(0) : '--'}</span>
                <span className="text-muted-foreground mx-1">|</span>
                <span className="text-purple-400">TPO={tpoPoc > 0 ? tpoPoc.toFixed(0) : '--'}</span>
              </span>
            </div>
            {pocDivergence > 0 && (
              <span className={`text-[8px] font-mono px-1.5 py-0.5 rounded ${
                pocDivergence > 50 ? 'bg-yellow-500/20 text-yellow-400' : 'text-muted-foreground'
              }`}>
                Δ {pocDivergence.toFixed(0)} pts
              </span>
            )}
          </div>

          {/* Component Diff Table */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-[7px] text-muted-foreground px-1 mb-1">
              <span>Component</span>
              <div className="flex items-center gap-3">
                <span className="w-12 text-center">Active</span>
                <span className="w-12 text-center text-purple-400">Shadow</span>
                <span className="w-10 text-center">Diff</span>
              </div>
            </div>
            {Object.entries(componentConfig).map(([key, config]) => {
              const activeVal = componentScores[key] || 0;
              const shadowVal = shadowComponents[key] || 0;
              const diff = shadowComponentDiffs[key] || 0;
              const isDifferent = Math.abs(diff) > 1;
              
              return (
                <div key={key} className={`flex items-center justify-between px-1 py-0.5 rounded ${
                  isDifferent ? 'bg-purple-500/10' : ''
                }`}>
                  <div className="flex items-center gap-1 text-[8px]">
                    <span>{config.icon}</span>
                    <span className={isDifferent ? 'text-purple-300' : 'text-muted-foreground'}>
                      {config.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[8px] font-mono">
                    <span className={`w-12 text-center ${
                      activeVal > 5 ? 'text-green-400' : activeVal < -5 ? 'text-red-400' : 'text-slate-400'
                    }`}>
                      {activeVal > 0 ? '+' : ''}{activeVal.toFixed(1)}
                    </span>
                    <span className={`w-12 text-center ${
                      shadowVal > 5 ? 'text-green-400' : shadowVal < -5 ? 'text-red-400' : 'text-purple-400'
                    }`}>
                      {shadowVal > 0 ? '+' : ''}{shadowVal.toFixed(1)}
                    </span>
                    <span className={`w-10 text-center font-bold ${
                      diff > 3 ? 'text-green-400' : diff < -3 ? 'text-red-400' : 'text-muted-foreground'
                    }`}>
                      {isDifferent ? (diff > 0 ? '+' : '') + diff.toFixed(1) : '—'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Divergence Explanation */}
          {hasDivergenceAlert && (
            <div className="mt-2 px-2 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded text-[8px] text-yellow-400">
              <span className="font-semibold">⚡ SIGNAL DIVERGENCE:</span>{' '}
              Shadow (TPO-based) would trigger <span className="font-bold">{shadowDirection}</span> but Active (Volume-based) is silent.
              <div className="text-[7px] text-yellow-400/70 mt-0.5">
                This indicates the architectural fix would generate a signal here.
              </div>
            </div>
          )}

          {/* Footer explanation */}
          <div className="mt-2 text-[7px] text-purple-400/60 text-center bg-black/20 rounded px-2 py-1">
            Shadow uses TPO POC (time-based) | Active uses Volume POC (CVD-dependent)
          </div>
          </>
        )}
      </div>
      )}

      {/* ============================================
          SECONDARY WIDGET: INSTITUTIONAL 3-LAYER SCORING
          ============================================ */}
      <div className="bg-surface/30 border border-border/50 rounded p-2">
        {/* Header - Cleaner layout */}
        <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-border/30">
          <span className="text-[9px] uppercase text-muted-foreground font-semibold tracking-wide">
            Institutional 3-Layer
          </span>
          <span className="text-[10px] text-accent font-mono font-medium">
            ₹{price?.toLocaleString() || '--'}
          </span>
        </div>

        {/* SESSION 112: LIQUIDITY & FLOW moved to Left Sidebar */}

        {/* SESSION 112: MARKET PROFILE moved to Top Bar */}

        {/* SESSION 82: INITIAL BALANCE (IB) LEVELS - Only show when locked */}
        {ibLocked && ibHigh > 0 && ibLow > 0 && (
          <div className="mb-2" data-testid="ib-levels-panel">
            <div className="text-[8px] text-teal-400 font-semibold uppercase mb-1.5 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
              Initial Balance (IB)
              <span className="ml-auto text-[7px] font-mono text-muted-foreground">10:15 Lock</span>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {/* IB High */}
              <div className="text-center py-1 bg-green-500/10 border border-green-500/20 rounded">
                <div className="text-[6px] text-green-400 font-medium">IB HIGH</div>
                <div className="text-xs font-bold font-mono text-foreground">
                  {ibHigh > 0 ? ibHigh.toLocaleString() : '--'}
                </div>
              </div>
              {/* IB Low */}
              <div className="text-center py-1 bg-red-500/10 border border-red-500/20 rounded">
                <div className="text-[6px] text-red-400 font-medium">IB LOW</div>
                <div className="text-xs font-bold font-mono text-foreground">
                  {ibLow > 0 ? ibLow.toLocaleString() : '--'}
                </div>
              </div>
            </div>
            {/* IB Width + Regime */}
            <div className="mt-1 flex items-center justify-between text-[7px]">
              <span className="text-muted-foreground">
                Width: <span className="font-mono font-medium text-teal-400">{ibWidth.toFixed(0)} pts</span>
              </span>
              <span className={`px-1.5 py-0.5 rounded font-medium ${regimeConfig.color}`}>
                {regimeConfig.label}
              </span>
            </div>
          </div>
        )}

        {/* SESSION 112: IMBALANCE STATE moved to Left Sidebar */}

        {/* SESSION 112: GEX ANCHOR moved to Left Sidebar */}

        {/* SESSION 83: HISTORICAL MAGNETS (Naked POCs) */}
        {nakedPocsCount > 0 && (
          <div className="mb-2" data-testid="naked-pocs-panel">
            <div className="text-[8px] text-yellow-400 font-semibold uppercase mb-1.5 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400"></span>
              Historical Magnets (nPOCs)
              <span className="ml-auto text-[7px] font-mono text-muted-foreground">
                {nakedPocsCount} active | {historicalDaysLoaded} days
              </span>
            </div>
            <div className="grid gap-1 max-h-24 overflow-y-auto pr-1">
              {nakedPocs.slice(0, 5).map((npoc, index) => (
                <div 
                  key={`npoc-${index}`}
                  className={`flex items-center justify-between px-2 py-1 rounded border text-[9px] transition-all ${
                    price > 0 && npoc.price > price 
                      ? 'bg-green-500/10 border-green-500/30'  // Above current price
                      : 'bg-red-500/10 border-red-500/30'      // Below current price
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`font-bold ${price > 0 && npoc.price > price ? 'text-green-400' : 'text-red-400'}`}>
                      {price > 0 && npoc.price > price ? '↑' : '↓'}
                    </span>
                    <span className="font-mono font-bold text-foreground">
                      ₹{npoc.price?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-muted-foreground text-[7px]">
                      {npoc.date?.slice(5)}
                    </span>
                    {price > 0 && (
                      <span className={`font-mono text-[7px] px-1 rounded ${
                        Math.abs(price - npoc.price) < 50 
                          ? 'bg-yellow-500/30 text-yellow-400' 
                          : 'text-muted-foreground'
                      }`}>
                        {Math.abs(price - npoc.price).toFixed(0)} pts
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1 text-[7px] text-muted-foreground text-center bg-black/20 rounded px-2 py-0.5">
              🎯 TREND_MODE: Price breaks IB → Target nPOC
            </div>
          </div>
        )}

        {/* GATES Status - Clean layout */}
        <div className="flex items-center gap-2 text-[8px] border-t border-border/30 pt-1.5 mb-1.5">
          <span className="text-muted-foreground font-medium">Gates:</span>
          <div className={`px-1.5 py-0.5 rounded text-[7px] font-bold transition-all duration-300 ${
            isVetoed 
              ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
              : 'bg-green-500/20 text-green-400 border border-green-500/30'
          }`}>
            {isVetoed ? 'BLOCKED' : 'CLEAR'}
          </div>
          {vetoReason && (
            <span className="text-orange-400/80 font-mono text-[7px] truncate flex-1" title={vetoReason}>
              {vetoReason}
            </span>
          )}
        </div>

        {/* SESSION 112: VIX moved to Left Sidebar - keeping only Target here */}
        <div className="flex items-center justify-end text-[8px] border-t border-border/30 pt-1.5 mb-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Target:</span>
            <span className="font-mono font-bold text-accent">{Math.round(threshold)}</span>
          </div>
        </div>

        {/* State & Action Row - Cleaner */}
        <div className="flex items-center justify-between text-[8px] border-t border-border/30 pt-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">State:</span>
            <span className={`font-mono font-bold ${stateConfig.color.split(' ')[0]}`}>
              {state}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Action:</span>
            <span className="font-mono font-bold text-foreground">
              {action.replace(/_/g, ' ')}
            </span>
          </div>
        </div>

        {/* Active Position (if any) - SESSION 89: Explicit FUTURES labeling */}
        {position && state === 'ACTIVE' && (
          <div className="mt-2 pt-1.5 border-t border-border/30">
            <div className="flex items-center justify-between text-[9px]">
              <span className="text-muted-foreground">
                Position: <span className={`font-medium ${position.direction === 'LONG' ? 'text-green-400' : 'text-red-400'}`}>
                  {position.direction}
                </span> @ {position.entry_price?.toFixed(2)} <span className="text-cyan-400/70">(Futures)</span>
              </span>
              <span className={`font-mono font-bold ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                P&L: {pnl >= 0 ? '+' : ''}{pnl?.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* ============================================
            SESSION 81: ACTIVE RISK & TARGET PANEL
            - Structural SL (Imbalance Shield)
            - Liquidity Target (Liquidity Magnet)
            Only visible when position_state is LONG or SHORT
            ============================================ */}
        {isInPosition && (
          <div className={`mt-3 pt-2 border-t rounded p-2 transition-all duration-300 ${
            positionStateMachine === 'LONG' 
              ? 'border-green-500/40 bg-green-500/5' 
              : 'border-red-500/40 bg-red-500/5'
          }`} data-testid="active-risk-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] uppercase text-cyan-400 font-semibold tracking-wide flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full animate-pulse ${positionStateMachine === 'LONG' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                Active Risk & Target
              </span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                positionStateMachine === 'LONG' 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/40' 
                  : 'bg-red-500/20 text-red-400 border border-red-500/40'
              }`}>
                {positionStateMachine === 'LONG' ? '↑ LONG' : '↓ SHORT'}
              </span>
            </div>
            
            {/* SL and TP Grid */}
            <div className="grid grid-cols-2 gap-2">
              {/* SESSION 89: Structural SL with Trailing SL Visualizer */}
              <div className={`text-center p-2 border rounded transition-all duration-300 ${
                trailingSLActive 
                  ? 'bg-green-500/10 border-green-500/40' 
                  : 'bg-red-500/10 border-red-500/30'
              }`} data-testid="structural-sl">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <span className={trailingSLActive ? 'text-green-400' : 'text-red-400'}>
                    {trailingSLActive ? '🔒' : '🛡️'}
                  </span>
                  <span className={`text-[8px] font-semibold uppercase ${
                    trailingSLActive ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {trailingSLActive ? 'Trailing SL' : 'Stop Loss'}
                  </span>
                </div>
                {/* SESSION 89: Show Trailing SL price when active, else show structural SL */}
                <div className={`text-lg font-mono font-bold ${
                  trailingSLActive ? 'text-green-300' : 'text-red-300'
                }`}>
                  ₹{trailingSLActive && trailingSLPrice > 0 
                    ? trailingSLPrice.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 2})
                    : (structuralSL > 0 ? structuralSL.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 2}) : '--')
                  }
                </div>
                <div className={`text-[7px] mt-0.5 ${trailingSLActive ? 'text-green-400/70' : 'text-red-400/70'}`}>
                  {trailingSLActive 
                    ? (highTrendConviction ? '🚀 Trend Mode Active' : 'Trailing 🔒')
                    : 'Imbalance Shield'
                  }
                </div>
                {/* Distance to SL */}
                {((trailingSLActive && trailingSLPrice > 0) || structuralSL > 0) && price > 0 && (
                  <div className={`text-[8px] font-mono mt-1 px-1.5 py-0.5 rounded ${
                    positionStateMachine === 'LONG'
                      ? ((trailingSLActive ? price - trailingSLPrice : price - structuralSL) < 10 
                          ? (trailingSLActive ? 'bg-green-500/30 text-green-300' : 'bg-red-500/30 text-red-300') 
                          : 'text-muted-foreground')
                      : ((trailingSLActive ? trailingSLPrice - price : structuralSL - price) < 10 
                          ? (trailingSLActive ? 'bg-green-500/30 text-green-300' : 'bg-red-500/30 text-red-300') 
                          : 'text-muted-foreground')
                  }`}>
                    {positionStateMachine === 'LONG' 
                      ? `${((trailingSLActive ? price - trailingSLPrice : price - structuralSL)).toFixed(1)} pts away` 
                      : `${((trailingSLActive ? trailingSLPrice - price : structuralSL - price)).toFixed(1)} pts away`}
                  </div>
                )}
                {/* SESSION 89: (Futures) label */}
                <div className="text-[6px] text-cyan-400/50 mt-1">(Futures)</div>
              </div>
              
              {/* Liquidity Target (Liquidity Magnet) - SESSION 89: Show trend mode override */}
              <div className={`text-center p-2 border rounded ${
                trailingSLActive && highTrendConviction
                  ? 'bg-purple-500/10 border-purple-500/30'
                  : 'bg-cyan-500/10 border-cyan-500/30'
              }`} data-testid="liquidity-target">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <span className={trailingSLActive && highTrendConviction ? 'text-purple-400' : 'text-cyan-400'}>
                    {trailingSLActive && highTrendConviction ? '🚀' : '🧲'}
                  </span>
                  <span className={`text-[8px] font-semibold uppercase ${
                    trailingSLActive && highTrendConviction ? 'text-purple-400' : 'text-cyan-400'
                  }`}>
                    {trailingSLActive && highTrendConviction ? 'TREND MODE' : 'Take Profit'}
                  </span>
                </div>
                <div className={`text-lg font-mono font-bold ${
                  trailingSLActive && highTrendConviction ? 'text-purple-300' : 'text-cyan-300'
                }`}>
                  {trailingSLActive && highTrendConviction
                    ? 'Trailing'
                    : `₹${liquidityTarget > 0 ? liquidityTarget.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 2}) : '--'}`
                  }
                </div>
                <div className={`text-[7px] mt-0.5 ${
                  trailingSLActive && highTrendConviction ? 'text-purple-400/70' : 'text-cyan-400/70'
                }`}>
                  {trailingSLActive && highTrendConviction 
                    ? 'Fixed target skipped 🔥'
                    : 'Liquidity Magnet'
                  }
                </div>
                {/* Distance to TP - only show if NOT in trend mode */}
                {!highTrendConviction && liquidityTarget > 0 && price > 0 && (
                  <div className={`text-[8px] font-mono mt-1 px-1.5 py-0.5 rounded ${
                    positionStateMachine === 'LONG'
                      ? (liquidityTarget - price < 10 ? 'bg-cyan-500/30 text-cyan-300' : 'text-muted-foreground')
                      : (price - liquidityTarget < 10 ? 'bg-cyan-500/30 text-cyan-300' : 'text-muted-foreground')
                  }`}>
                    {positionStateMachine === 'LONG' 
                      ? `${(liquidityTarget - price).toFixed(1)} pts to target` 
                      : `${(price - liquidityTarget).toFixed(1)} pts to target`}
                  </div>
                )}
                {/* SESSION 89: (Futures) label */}
                <div className="text-[6px] text-cyan-400/50 mt-1">(Futures)</div>
              </div>
            </div>
            
            {/* Exit Rules Reminder - SESSION 89: Updated for trailing SL */}
            <div className="mt-2 text-[7px] text-muted-foreground text-center bg-black/20 rounded px-2 py-1">
              {trailingSLActive ? (
                <>
                  <span className="text-green-400">🔒 TRAILING:</span> SL follows price | 
                  {highTrendConviction 
                    ? <span className="text-purple-400 ml-1">🚀 TREND:</span>
                    : <span className="text-cyan-400 ml-1">🧲 MAGNET:</span>
                  }
                  {highTrendConviction ? ' Let it run!' : ' Price hits TP → Exit'}
                </>
              ) : (
                <>
                  <span className="text-red-400">🛡️ SHIELD:</span> Price hits SL → Exit | 
                  <span className="text-cyan-400 ml-1">🧲 MAGNET:</span> Price hits TP → Exit
                </>
              )}
            </div>
          </div>
        )}

        {/* ============================================
            SESSION 70: TRIDENT EXIT PANEL (Bimodal)
            ============================================ */}
        {positionState && (
          <div className="mt-3 pt-2 border-t border-purple-500/30 bg-purple-500/5 rounded p-2" data-testid="trident-exit-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] uppercase text-purple-400 font-semibold tracking-wide flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></span>
                Trident Exit Active
              </span>
              <span className={`text-[9px] font-bold ${
                positionState.direction === 'LONG' ? 'text-green-400' : 'text-red-400'
              }`}>
                {positionState.direction}
              </span>
            </div>
            
            {/* Live P&L */}
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="text-center p-1.5 bg-black/30 rounded">
                <div className="text-[7px] text-muted-foreground">Entry Price</div>
                <div className="text-sm font-mono font-bold text-foreground">
                  ₹{positionState.entry_price?.toLocaleString() || '--'}
                </div>
              </div>
              <div className="text-center p-1.5 bg-black/30 rounded">
                <div className="text-[7px] text-muted-foreground">Live P&L</div>
                <div className={`text-sm font-mono font-bold ${
                  price && positionState.entry_price
                    ? ((positionState.direction === 'LONG' ? price - positionState.entry_price : positionState.entry_price - price) >= 0 
                        ? 'text-green-400' 
                        : 'text-red-400')
                    : 'text-foreground'
                }`}>
                  {(() => {
                    if (!price || !positionState.entry_price) return '--';
                    const livePnL = positionState.direction === 'LONG' 
                      ? price - positionState.entry_price 
                      : positionState.entry_price - price;
                    return `${livePnL >= 0 ? '+' : ''}${livePnL.toFixed(2)}`;
                  })()}
                </div>
              </div>
            </div>
            
            {/* Trailing SL & Max Price */}
            <div className="grid grid-cols-2 gap-2">
              <div className="text-center p-1.5 bg-red-500/10 border border-red-500/30 rounded">
                <div className="text-[7px] text-red-400">Trailing SL</div>
                <div className="text-xs font-mono font-bold text-red-300">
                  ₹{trailingSL?.toFixed(0) || (atrValue ? (positionState.direction === 'LONG' ? (maxFavorablePrice - atrValue * 1.5).toFixed(0) : (maxFavorablePrice + atrValue * 1.5).toFixed(0)) : '--')}
                </div>
              </div>
              <div className="text-center p-1.5 bg-purple-500/10 border border-purple-500/30 rounded">
                <div className="text-[7px] text-purple-400">Max Favorable</div>
                <div className="text-xs font-mono font-bold text-purple-300">
                  ₹{maxFavorablePrice?.toLocaleString() || positionState.entry_price?.toLocaleString() || '--'}
                </div>
              </div>
            </div>
            
            {/* Exit Conditions */}
            <div className="mt-2 text-[7px] text-gray-500 text-center">
              <span className="text-purple-400">SOFT:</span> EMA &lt; {finalThreshold - 40} for 10s | 
              <span className="text-red-400 ml-1">HARD:</span> Price ≤ SL
            </div>
          </div>
        )}
        
        {/* SESSION 70: Last Exit Indicator - Shows recent bimodal exit */}
        {lastExit && !positionState && (
          <div className="mt-3 pt-2 border-t border-amber-500/30 bg-amber-500/5 rounded p-2" data-testid="last-exit-panel">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] uppercase text-amber-400 font-semibold tracking-wide">
                Last Exit
              </span>
              <span className={`text-[9px] font-bold ${
                lastExit.exit_type === 'HARD' ? 'text-red-400' : 'text-purple-400'
              }`}>
                {lastExit.exit_type === 'HARD' ? '🛑 HARD SL' : '⚠️ SOFT DECAY'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="p-1 bg-black/30 rounded">
                <div className="text-[6px] text-muted-foreground">Entry</div>
                <div className="text-[10px] font-mono text-foreground">₹{lastExit.entry_price?.toFixed(0) || '--'}</div>
              </div>
              <div className="p-1 bg-black/30 rounded">
                <div className="text-[6px] text-muted-foreground">Exit</div>
                <div className="text-[10px] font-mono text-foreground">₹{lastExit.exit_price?.toFixed(0) || '--'}</div>
              </div>
              <div className={`p-1 rounded ${lastExit.pnl >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <div className="text-[6px] text-muted-foreground">P&L</div>
                <div className={`text-[10px] font-mono font-bold ${lastExit.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {lastExit.pnl >= 0 ? '+' : ''}{lastExit.pnl?.toFixed(2) || '--'}
                </div>
              </div>
            </div>
            <div className="mt-1 text-[6px] text-gray-600 text-center">
              {lastExit.reason || lastExit.exit_reason}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

GodScoreCommandCenter.displayName = 'GodScoreCommandCenter';

export default GodScoreCommandCenter;
